import { useEffect, useState } from 'react';
import { getToken } from '../utils/tokenStorage';

/**
 * GDPR/ePrivacy cookie consent banner.
 *
 * Behaviour mirrors endokrinoloog.ee:
 *  - Floating card pinned to the bottom-right.
 *  - Two clear actions: "Accept all" (cyan) + "Only necessary" (ghost).
 *  - A close button (×) is treated as "Only necessary".
 *  - Persists the choice in localStorage under `fitness_cookie_consent`
 *    (values: "all" | "necessary"). When set the banner stays hidden
 *    on every subsequent visit.
 *  - On "Accept all" we dispatch a `cookie-consent-granted` window
 *    event so the PostHog + Clarity loaders in `public/index.html`
 *    can boot immediately without requiring a page refresh.
 *
 * The banner is intentionally mounted at App-root so it shows on every
 * unauthenticated page (landing, blog, pricing, …) until the user makes
 * a choice.
 */

const STORAGE_KEY = 'fitness_cookie_consent';
const EVENT_GRANTED = 'cookie-consent-granted';

type Consent = 'all' | 'necessary' | null;

function readConsent(): Consent {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'all' || v === 'necessary') return v;
    return null;
  } catch {
    return null;
  }
}

export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  // The banner is pinned bottom-right, which sat directly on top of the
  // right-most bottom-nav button (Profile / Log out) inside the app. Lift it
  // above the taskbar whenever one is mounted.
  const [aboveTaskbar, setAboveTaskbar] = useState(false);

  useEffect(() => {
    if (readConsent() === null) {
      // Tiny delay so the banner animates in after the page hero, not on
      // top of the first paint.
      const t = setTimeout(() => {
        // Signed-in screens always carry a bottom taskbar; the banner used to
        // sit right on top of its right-most button.
        setAboveTaskbar(!!getToken());
        setVisible(true);
      }, 700);
      return () => clearTimeout(t);
    }
    return undefined;
  }, []);

  // The user can sign in while the banner is still up — re-check so it lifts
  // above the taskbar that just appeared.
  useEffect(() => {
    if (!visible || aboveTaskbar) return undefined;
    const id = setInterval(() => {
      if (getToken()) setAboveTaskbar(true);
    }, 1500);
    return () => clearInterval(id);
  }, [visible, aboveTaskbar]);

  // iter359 — a Radix dialog (Add Entry, Personal panel…) renders ABOVE this
  // banner's slot on mobile, so the card covered the modal's submit button.
  // Step aside while a dialog is open; it comes back when the modal closes.
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (!visible) return undefined;
    const id = setInterval(() => {
      setDialogOpen(
        document.body.hasAttribute('data-scroll-locked')
        || !!document.querySelector('[role="dialog"][data-state="open"]'),
      );
    }, 400);
    return () => clearInterval(id);
  }, [visible]);

  const persist = (choice: 'all' | 'necessary') => {
    try {
      localStorage.setItem(STORAGE_KEY, choice);
    } catch {
      /* private-mode browser — best effort, banner won't reappear this session */
    }
    if (choice === 'all') {
      try {
        window.dispatchEvent(new CustomEvent(EVENT_GRANTED));
      } catch {
        /* noop */
      }
    }
    setVisible(false);
  };

  if (!visible || dialogOpen) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-banner"
      className={`fixed right-4 z-[9999] max-w-sm w-[calc(100vw-2rem)] sm:w-96 animate-[cookie-in_280ms_ease-out] ${
        aboveTaskbar ? 'bottom-[calc(6.5rem+env(safe-area-inset-bottom,0px))]' : 'bottom-4'
      }`}
    >
      <style>{`
        @keyframes cookie-in {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div className="relative bg-[#0f141a] border border-[#20262f] rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.55)] p-4 pr-9">
        {/* Close (treated as "Only necessary") */}
        <button
          type="button"
          aria-label="Dismiss — only necessary cookies"
          onClick={() => persist('necessary')}
          data-testid="cookie-banner-close"
          className="absolute top-2 right-2 w-7 h-7 rounded-full text-[#a8abb3] hover:text-white hover:bg-[#20262f] transition flex items-center justify-center"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        {/* Title with cookie icon */}
        <div className="flex items-center gap-2 mb-1.5">
          <span aria-hidden="true" className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#cafd00]/15 text-[#cafd00]">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5Z" />
              <path d="M8.5 8.5h.01M16 15.5h.01M9 14.5h.01M14.5 11h.01" />
            </svg>
          </span>
          <h3 className="text-sm font-semibold text-[#f1f3fc]">This site uses cookies</h3>
        </div>

        {/* Body */}
        <p className="text-xs text-[#a8abb3] leading-relaxed mb-3">
          We use cookies to improve your experience and for anonymous analytics. Necessary
          cookies are always enabled. Statistics cookies are only enabled with your consent.{' '}
          <a
            href="/privacy.html"
            className="text-[#cafd00] hover:underline"
            data-testid="cookie-banner-privacy-link"
          >
            Read our privacy policy
          </a>
          .
        </p>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => persist('all')}
            data-testid="cookie-banner-accept-all"
            className="flex-1 h-9 rounded-full bg-[#cafd00] hover:bg-[#b8e600] text-[#0a0e14] text-xs font-bold uppercase tracking-wider transition"
          >
            Accept all
          </button>
          <button
            type="button"
            onClick={() => persist('necessary')}
            data-testid="cookie-banner-necessary-only"
            className="flex-1 h-9 rounded-full bg-transparent border border-[#2c333d] text-[#f1f3fc] hover:border-[#3a4250] hover:bg-[#1a1f26] text-xs font-semibold transition"
          >
            Only necessary
          </button>
        </div>
      </div>
    </div>
  );
}

export default CookieBanner;
